import { useCallback, useEffect, useRef, useState } from "react";
import api from "../../../api";
import useTtsPlayback from "./useTtsPlayback";
import useMicRecorder from "./useMicRecorder";
import { questionText, speechChunks } from "./voiceSurveyFlow";

// Drives VoiceQuestionScreen through the open-text questions of a VOICE-mode survey.
//
// No model in the loop. Each question is read aloud (POST /collector/tts/), and
// the respondent's answer goes into an editable `draft`: typed, or spoken and
// transcribed (POST /collector/transcribe/) into it. Next hands the draft to
// `onAnswer` and moves on; Back returns to the previous question with its answer
// in the draft again (`getAnswer`). Nothing here talks to a session.
//
// One instance lives for the whole survey (the runner owns it) so the audio
// element unlocked in a click stays unlocked across pages.
// runStage(questions, { onDone, onBack, startAt }) starts a run of questions:
// onDone fires after the last one, onBack on Back from the first one.
//
// readAloud(id, text) reads a content block on request (never by default), to
// its end unless stopReading() or anything else that speaks cuts it short.
// `readingId` is the block being read, or null.
//
// The builder's preview has no respondent token, so it passes `synthesize(text,
// signal)` and `transcribeAudio(blob)` to use its signed-in routes instead.
//
// `quiet` (the respondent's quiet mode): questions show at once, in full, and
// nothing is read unless asked for (Replay, a content block's Listen). Turning it
// on mid-question stops the voice. `voiced` says whether the question on screen
// is being / was read aloud (so its caption types out with the voice).

// How long a question waits for its voice before the respondent's turn opens
// anyway (the caption is already on screen).
export const VOICE_GRACE_MS = 10000;

export default function useVoiceSurveyController({ surveyId, token, onAnswer, getAnswer, synthesize: synthesizeOverride, transcribeAudio, quiet = false }) {
    const synthesize = useCallback(
        (text, _voice, signal) =>
            synthesizeOverride ? synthesizeOverride(text, signal) : api.fetchSurveyTts(surveyId, token, text, signal),
        [surveyId, token, synthesizeOverride]
    );
    const tts = useTtsPlayback({ synthesize });

    // orbMode: idle | speaking | listening | thinking (saving an answer)
    const [orbMode, setOrbMode] = useState("idle");
    const [question, setQuestion] = useState("");
    const [utteranceKey, setUtteranceKey] = useState(0);
    const [index, setIndex] = useState(0);
    const [total, setTotal] = useState(0);
    const [turnInFlight, setTurnInFlight] = useState(false);
    const [draft, setDraft] = useState("");
    const [ready, setReady] = useState(false); // the current question's voice is in hand
    const [readingId, setReadingId] = useState(null);
    const [voiced, setVoiced] = useState(true);
    const quietRef = useRef(quiet);
    quietRef.current = quiet;

    const questionsRef = useRef([]);
    const indexRef = useRef(0);
    const onDoneRef = useRef(null);
    const onBackRef = useRef(null);
    const onAnswerRef = useRef(onAnswer);
    onAnswerRef.current = onAnswer;
    const getAnswerRef = useRef(getAnswer);
    getAnswerRef.current = getAnswer;
    const noAudioRef = useRef(false);
    const speakGenRef = useRef(0);
    const graceTimerRef = useRef(null);
    const prepareTimerRef = useRef(null);
    const proceedRef = useRef(null); // opens the turn for the utterance playing now
    const shownTextRef = useRef(null); // the question on screen
    const readGenRef = useRef(0);
    const waitingRef = useRef(false); // a question is waiting on its voice to show

    const mic = useMicRecorder({
        surveyId,
        token,
        transcribeAudio,
        // A take goes into the draft (after anything already typed), for Next.
        onResult: (text) => setDraft((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text)),
        // Any mic failure leaves the turn open: typing always works.
        onError: () => setOrbMode("listening"),
    });

    const listen = useCallback(() => setOrbMode("listening"), []);

    // Read question `i` of the current stage aloud, then open the respondent's
    // turn — when the audio ends, when it fails, after VOICE_GRACE_MS, or when the
    // respondent stops it.
    //
    // A new question isn't shown until its voice is in hand (`ready` stays false
    // meanwhile, so the screen never appears and then waits on silence); then it
    // shows and plays at once. A voice slower than VOICE_GRACE_MS shows the
    // question anyway. Replay re-speaks the question on screen without hiding it.
    // In quiet mode the question shows at once and the turn opens, unread
    // (`{ voice: true }` reads it anyway: Replay).
    const speakAt = useCallback(
        (i, { voice = false } = {}) => {
            const step = questionsRef.current[i];
            if (!step) return;
            readGenRef.current += 1;
            setReadingId(null);
            indexRef.current = i;
            const text = questionText(step);
            const gen = ++speakGenRef.current;
            clearTimeout(graceTimerRef.current);
            clearTimeout(prepareTimerRef.current);
            const fresh = shownTextRef.current !== text;
            waitingRef.current = false;

            if (quietRef.current && !voice) {
                tts.stop();
                proceedRef.current = null;
                shownTextRef.current = text;
                setIndex(i);
                setQuestion(text);
                if (fresh) setDraft(getAnswerRef.current?.(step) || "");
                setVoiced(false);
                setUtteranceKey((k) => k + 1);
                setReady(true);
                noAudioRef.current = true;
                listen();
                return;
            }
            if (fresh) setReady(false);

            const speak = () => {
                if (gen !== speakGenRef.current) return;
                waitingRef.current = false;
                shownTextRef.current = text;
                setIndex(i);
                setQuestion(text);
                if (fresh) setDraft(getAnswerRef.current?.(step) || "");
                setOrbMode("speaking");
                setVoiced(true);
                setUtteranceKey((k) => k + 1);
                setReady(true);
                noAudioRef.current = false;
                let settled = false;
                const proceed = () => {
                    if (settled || gen !== speakGenRef.current) return;
                    settled = true;
                    clearTimeout(graceTimerRef.current);
                    listen();
                };
                proceedRef.current = proceed;
                graceTimerRef.current = setTimeout(function onGrace() {
                    if (settled || gen !== speakGenRef.current) return;
                    if (tts.isMidUtterance()) {
                        graceTimerRef.current = setTimeout(onGrace, VOICE_GRACE_MS);
                        return;
                    }
                    tts.stop();
                    noAudioRef.current = true;
                    proceed();
                }, VOICE_GRACE_MS);
                tts.play(text, null)
                    .then(proceed)
                    .catch(() => {
                        noAudioRef.current = true;
                        proceed();
                    });
                // Warm the next question while this one is answered.
                const next = questionsRef.current[i + 1];
                if (next && !quietRef.current) tts.prefetch(questionText(next), null);
            };

            waitingRef.current = true;
            const voiceReady = new Promise((resolve) => {
                prepareTimerRef.current = setTimeout(resolve, VOICE_GRACE_MS);
                tts.prefetch(text, null).then(resolve);
            });
            voiceReady.then(() => {
                clearTimeout(prepareTimerRef.current);
                speak();
            });
        },
        [tts, listen]
    );

    // Start fetching a question's voice ahead of its stage (from the page before
    // it), so it shows without a wait.
    const prepare = useCallback(
        (step) => {
            if (step && !quietRef.current) tts.prefetch(questionText(step), null);
        },
        [tts]
    );

    // Leave the current stage: nothing still speaking may reopen it.
    const leave = useCallback(() => {
        speakGenRef.current += 1;
        waitingRef.current = false;
        clearTimeout(graceTimerRef.current);
        clearTimeout(prepareTimerRef.current);
        shownTextRef.current = null;
        proceedRef.current = null;
        mic.cancel();
        tts.stop();
        setOrbMode("idle");
    }, [mic, tts]);

    const advance = useCallback(() => {
        const next = indexRef.current + 1;
        if (next < questionsRef.current.length) {
            speakAt(next);
            return;
        }
        leave();
        const done = onDoneRef.current;
        done && done();
    }, [speakAt, leave]);

    const runStage = useCallback(
        (questions, { onDone, onBack, startAt = 0 } = {}) => {
            questionsRef.current = questions || [];
            onDoneRef.current = onDone;
            onBackRef.current = onBack;
            setTotal(questionsRef.current.length);
            if (questionsRef.current.length === 0) {
                onDone && onDone();
                return;
            }
            speakAt(Math.min(Math.max(0, startAt), questionsRef.current.length - 1));
        },
        [speakAt]
    );

    // Next: hand the answer (blank = none) to `onAnswer`, then move on. Saving is
    // the caller's; a failed save doesn't hold the respondent up, because the
    // final submit sends every answer again.
    const next = useCallback(
        async (raw) => {
            const text = (raw || "").trim();
            const step = questionsRef.current[indexRef.current];
            if (!step || turnInFlight) return;
            mic.cancel();
            tts.stop();
            setTurnInFlight(true);
            setOrbMode("thinking");
            try {
                await onAnswerRef.current?.(step, text);
            } catch (e) {
                /* kept in the runner's answers; re-sent on submit */
            } finally {
                setTurnInFlight(false);
            }
            advance();
        },
        [mic, tts, turnInFlight, advance]
    );

    // Back: the previous question of this stage, or out of it (onBack).
    const back = useCallback(() => {
        if (indexRef.current > 0) {
            mic.cancel();
            tts.stop();
            speakAt(indexRef.current - 1);
            return;
        }
        const onBack = onBackRef.current;
        if (!onBack) return;
        leave();
        onBack();
    }, [mic, tts, speakAt, leave]);

    // Record during the voice too: it stops, and the take starts.
    const startRecording = useCallback(() => {
        if (orbMode !== "listening" && orbMode !== "speaking") return;
        if (orbMode === "speaking") {
            tts.stop();
            proceedRef.current?.();
        }
        tts.wake();
        mic.start();
    }, [orbMode, tts, mic]);

    // The orb's two actions: stop the voice (the turn opens at once), or replay it.
    const stopSpeaking = useCallback(() => {
        tts.stop();
        proceedRef.current?.();
    }, [tts]);

    const replay = useCallback(() => {
        if (mic.status === "recording" || mic.status === "transcribing") return;
        tts.wake();
        speakAt(indexRef.current, { voice: true });
    }, [mic.status, tts, speakAt]);

    // "Tap to enable sound": a fresh gesture, so unlock and re-read the question.
    const enableSound = useCallback(() => {
        tts.clearBlocked();
        tts.unlock();
        if (mic.status === "idle") speakAt(indexRef.current, { voice: true });
    }, [tts, mic.status, speakAt]);

    // Read a content block aloud, piece by piece (the TTS route caps a request's
    // text), until its end or until stopped. Starting another block stops this one.
    const stopReading = useCallback(() => {
        readGenRef.current += 1;
        tts.stop();
        setReadingId(null);
    }, [tts]);

    // Quiet mode turned on: whatever is being read stops (a content block too),
    // and a question's turn opens; one still waiting on its voice shows now.
    useEffect(() => {
        if (!quiet) return;
        stopReading();
        if (waitingRef.current) speakAt(indexRef.current);
        else proceedRef.current?.();
    }, [quiet, stopReading, speakAt]);

    const readAloud = useCallback(
        async (id, text) => {
            const gen = ++readGenRef.current;
            setReadingId(id);
            tts.wake();
            const chunks = speechChunks(text);
            try {
                for (let i = 0; i < chunks.length; i++) {
                    if (chunks[i + 1]) tts.prefetch(chunks[i + 1], null);
                    // A stopped play() never settles; the generation check below
                    // is what matters for anything that does.
                    await tts.play(chunks[i], null);
                    if (gen !== readGenRef.current) return;
                }
            } catch (e) {
                /* no audio: the text is on screen anyway */
            }
            if (gen === readGenRef.current) setReadingId(null);
        },
        [tts]
    );

    // Leaving the runner mid-question: no voice or mic left behind.
    useEffect(
        () => () => {
            clearTimeout(graceTimerRef.current);
            clearTimeout(prepareTimerRef.current);
            speakGenRef.current += 1;
        },
        []
    );

    return {
        unlock: tts.unlock, // call SYNCHRONOUSLY in a click (iOS autoplay)
        runStage,
        prepare,
        ready,
        next,
        back,
        current: questionsRef.current[index] || null,
        draft,
        setDraft,
        startRecording,
        submitRecording: mic.stop,
        stopSpeaking,
        replay,
        orbMode,
        question,
        utteranceKey,
        voiced,
        turnInFlight,
        index, // position in the current stage
        total, // questions in the current stage
        micStatus: mic.status,
        soundBlocked: tts.blocked,
        enableSound,
        readAloud,
        stopReading,
        readingId,
        ttsLevelRef: tts.levelRef,
        ttsStartedRef: tts.startedRef,
        ttsProgressRef: tts.progressRef,
        ttsNoAudioRef: noAudioRef,
        micAnalyserRef: mic.analyserRef,
    };
}
